export enum GradientType
{
  Vertical = 'vertical',
  Horizontal = 'horizontal',
  Diagonal = 'diagonal',
  Radial = 'radial'
}

export interface PatternBackgroundOptions
{
  tile_size?: number;
  tile_width?: number;
  tile_height?: number;
  background_color?: string | string[];
  gradient_type?: GradientType;
}

function drawPatternBackgound_justColor(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  options: PatternBackgroundOptions = {}
): void
{
  if ( !options.background_color )
    return;

  const colors = Array.isArray( options.background_color )
    ? options.background_color
    : [options.background_color];

  if ( colors.length === 1 )
  {
    ctx.fillStyle = colors[0];
    ctx.fillRect( x, y, width, height );
    return;
  }

  let grad: CanvasGradient;
  switch ( options.gradient_type )
  {
    case GradientType.Horizontal:
      grad = ctx.createLinearGradient( x, y, x + width, y );
      break
    case GradientType.Diagonal:
      grad = ctx.createLinearGradient( x, y, x + width, y + height );
      break
    case GradientType.Radial:
      grad = ctx.createRadialGradient(
        x + width / 2,
        y + height / 2,
        0,
        x + width / 2,
        y + height / 2,
        Math.max( width, height ) / 2
      );
      break
    default:
      grad = ctx.createLinearGradient( x, y, x, y + height );
  }

  colors.forEach( ( col, i ) => grad.addColorStop( i / ( colors.length - 1 ), col ) );

  ctx.fillStyle = grad;
  ctx.fillRect( x, y, width, height );
}

export function drawPatternBackground(
  ctx: CanvasRenderingContext2D,
  pattern_image: HTMLImageElement | ImageBitmap,
  x: number,
  y: number,
  width: number,
  height: number,
  options: PatternBackgroundOptions = {}
): void
{
  if ( pattern_image instanceof HTMLImageElement && ( !pattern_image.complete || pattern_image.naturalWidth === 0 ) )
    return;

  drawPatternBackgound_justColor( ctx, x, y, width, height, options );

  // Resolve target tile dimensions with backward compatibility for tile_size
  const tile_w = options.tile_width ?? options.tile_size;
  const tile_h = options.tile_height ?? options.tile_size;

  if ( tile_w && tile_h )
  {
    // If specific tiling is requested, render into a temporary canvas of that size
    const temp = document.createElement( 'canvas' );
    temp.width = tile_w;
    temp.height = tile_h;
    const tctx = temp.getContext( '2d' );
    if ( tctx )
    {
      tctx.drawImage( pattern_image, 0, 0, tile_w, tile_h );
      const p = ctx.createPattern( temp, 'repeat' );
      if ( p )
      {
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = p;
        ctx.fillRect( x, y, width, height );
        ctx.globalCompositeOperation = 'source-over';
        return;
      }
    }
  }

  // Fallback: use original image as repeating pattern
  const p = ctx.createPattern( pattern_image, 'repeat' );
  if ( p )
  {
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = p;
    ctx.fillRect( x, y, width, height );
    ctx.globalCompositeOperation = 'source-over';
  }
}